import { Context } from '@hotwired/stimulus';
import { emitter } from 'src/emitter';
import { createElement, createSvg } from 'src/utils/dom-create';
import { toggleAttribute, toggleHidden } from 'src/utils/dom-toggle';
import { validate as validateUuid } from 'uuid';
import {
  emitChangedModalLayout,
  emitPickedValue,
  EVENT_ESCAPE,
  EVENT_INPUT,
  EVENT_SET_QUERY,
  EVENT_SET_STATE,
  isActionPickValue,
  isEventInput,
  isEventSetQuery,
  isEventSetState,
} from './events';
import SearchBarModalController from './modal_controller';
import {
  TOKEN_DEFAULTS,
  TOKEN_TYPE_AFTER,
  TOKEN_TYPE_ALIAS_ASSET_TYPE,
  TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE,
  TOKEN_TYPE_ALIAS_LOCATED_AT,
  TOKEN_TYPE_ALIAS_NAME,
  TOKEN_TYPE_ASSIGNEE,
  TOKEN_TYPE_AUTHOR,
  TOKEN_TYPE_BEFORE,
  TOKEN_TYPE_CONFIDENTIAL,
  TOKEN_TYPE_INVALID,
  TOKEN_TYPE_LABEL,
  TOKEN_TYPE_RANGE,
  TOKEN_TYPE_REL,
  TOKEN_TYPE_REPORTED_AFTER,
  TOKEN_TYPE_REPORTED_BEFORE,
  TOKEN_TYPE_REPORTED_RANGE,
  TOKEN_TYPE_RESOLVED_AFTER,
  TOKEN_TYPE_RESOLVED_BEFORE,
  TOKEN_TYPE_RESOLVED_RANGE,
  TOKEN_TYPE_ROLE,
  TOKEN_TYPE_SITE,
  TOKEN_TYPE_TITLE,
  TokenType,
} from './tokens';

export const STATE_TOKEN_VALUE = 'token:value';
export const STATE_TOKEN_VALUE_ACTIVE = 'token:value:active';

const MEDIA_TYPE_COMBOBOX = 'text/vnd.kaboom.combobox-options';
const LABEL_VALIDATOR = /^[a-z][a-z0-9-_]*\/(\*|~|[a-z][a-z0-9-_]*)$/;
const TAG_VALIDATOR = /^tag:[-_.A-Za-z0-9,:+]+$/;

function validateLabel(value: string) {
  return LABEL_VALIDATOR.test(value);
}

function validateTag(value: string) {
  return TAG_VALIDATOR.test(value);
}

const OPTIONS: Record<
  TokenType,
  {
    readonly defaults: ReadonlyArray<
      string | Readonly<{ value: string; label: string }>
    >;
    format?: string;
    validate?: (value: string) => boolean;
  }
> = {
  [TOKEN_TYPE_ASSIGNEE]: {
    defaults: TOKEN_DEFAULTS[TOKEN_TYPE_ASSIGNEE],
    format: '{q}',
    validate: validateUuid,
  },
  [TOKEN_TYPE_AUTHOR]: {
    defaults: TOKEN_DEFAULTS[TOKEN_TYPE_AUTHOR],
    format: '{q}',
    validate: validateUuid,
  },
  [TOKEN_TYPE_LABEL]: { defaults: [], validate: validateLabel },
  [TOKEN_TYPE_TITLE]: { defaults: [] },
  [TOKEN_TYPE_ALIAS_NAME]: { defaults: [] },
  [TOKEN_TYPE_SITE]: { defaults: [], format: '{q}', validate: validateUuid },
  [TOKEN_TYPE_ALIAS_LOCATED_AT]: {
    defaults: [],
    format: '{q}',
    validate: validateUuid,
  },
  [TOKEN_TYPE_ROLE]: { defaults: [], format: '{q}', validate: validateTag },
  [TOKEN_TYPE_ALIAS_ASSET_TYPE]: {
    defaults: [],
    format: '{q}',
    validate: validateTag,
  },
  [TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE]: {
    defaults: [],
    format: '{q}',
    validate: validateTag,
  },
  [TOKEN_TYPE_REL]: { defaults: [] },
  [TOKEN_TYPE_CONFIDENTIAL]: {
    defaults: TOKEN_DEFAULTS[TOKEN_TYPE_CONFIDENTIAL],
  },
  [TOKEN_TYPE_BEFORE]: { defaults: [] },
  [TOKEN_TYPE_AFTER]: { defaults: [] },
  [TOKEN_TYPE_RANGE]: { defaults: [] },
  [TOKEN_TYPE_REPORTED_BEFORE]: { defaults: [] },
  [TOKEN_TYPE_REPORTED_AFTER]: { defaults: [] },
  [TOKEN_TYPE_REPORTED_RANGE]: { defaults: [] },
  [TOKEN_TYPE_RESOLVED_BEFORE]: { defaults: [] },
  [TOKEN_TYPE_RESOLVED_AFTER]: { defaults: [] },
  [TOKEN_TYPE_RESOLVED_RANGE]: { defaults: [] },
  [TOKEN_TYPE_INVALID]: { defaults: [] },
};

export default class SearchBarTokenValueModalController extends SearchBarModalController {
  public static values = {
    autocompleteUrls: Object,
    maxAutocompleteOptions: {
      type: Number,
      default: 12,
    },
  };

  declare private autocompleteUrlsValue: Partial<
    Record<'labels' | 'packages' | 'sites' | 'users', string>
  >;
  declare private maxAutocompleteOptionsValue: number;

  private lastTokenType: TokenType = TOKEN_TYPE_INVALID;
  private currentQueryOption!: HTMLButtonElement;
  private currentFormat: string | undefined;
  private currentValidator: ((value: string) => boolean) | undefined;
  private selectionGroup!: HTMLElement;
  private spinner!: SVGElement;
  private loadingRegion!: HTMLDivElement;
  private allowReselection: boolean = true;

  private mappedValues: Record<string, string> = {};

  private autocompleteTimer: NodeJS.Timeout | number | undefined;
  private autocompleteController: AbortController | undefined;

  constructor(context: Context) {
    super(context);

    this.onSearchBarStateChange = this.onSearchBarStateChange.bind(this);
    this.onSearchBarEscape = this.onSearchBarEscape.bind(this);
    this.onSearchBarInput = this.onSearchBarInput.bind(this);
    this.onSearchBarQuery = this.onSearchBarQuery.bind(this);
    this.onAutocompletedResponse = this.onAutocompletedResponse.bind(this);
    this.onAutocompleteHtml = this.onAutocompleteHtml.bind(this);
  }

  public connect(): void {
    super.connect();

    emitter.on(EVENT_SET_STATE, this.onSearchBarStateChange);
    emitter.on(EVENT_SET_QUERY, this.onSearchBarQuery);
    emitter.on(EVENT_INPUT, this.onSearchBarInput);
    emitter.on(EVENT_ESCAPE, this.onSearchBarEscape);
  }

  public disconnect(): void {
    super.disconnect();

    this.clearItems();

    emitter.off(EVENT_SET_STATE, this.onSearchBarStateChange);
    emitter.off(EVENT_SET_QUERY, this.onSearchBarQuery);
    emitter.off(EVENT_INPUT, this.onSearchBarInput);
    emitter.off(EVENT_ESCAPE, this.onSearchBarEscape);

    this.autocompleteController?.abort('Disconnected controller');

    if (this.autocompleteTimer) {
      clearTimeout(this.autocompleteTimer);
      this.autocompleteTimer = undefined;
    }
  }

  private onSearchBarStateChange(event: unknown) {
    if (!isEventSetState(event)) {
      return;
    }

    switch (event.state) {
      case STATE_TOKEN_VALUE: {
        this.createOptions(event.type, event.value, event.label);

        this.activeItem = null;
        this.active = true;
        break;
      }

      case STATE_TOKEN_VALUE_ACTIVE: {
        this.createOptions(event.type, event.value, event.label);

        if (!this.activeItem) {
          this.activateFirst();
        }
        this.active = true;
        break;
      }

      default: {
        this.activeItem = null;
        this.active = false;
        this.lastTokenType = TOKEN_TYPE_INVALID;
      }
    }
  }

  private onSearchBarEscape(_: unknown) {
    if (!this.active) {
      return;
    }

    // TODO: close active modals etc
  }

  private onSearchBarInput(event: unknown) {
    if (!this.active || !isEventInput(event)) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        this.activateNext();
        this.allowReselection = false;
        break;
      }

      case 'ArrowUp': {
        this.activatePrevious();
        this.allowReselection = false;
        break;
      }

      case 'Home': {
        this.activateFirst();
        this.allowReselection = false;
        break;
      }

      case 'End': {
        this.activateLast();
        this.allowReselection = false;
        break;
      }

      case 'Enter': {
        const submittableItem = this.activeItem || this.currentQueryOption;

        if (submittableItem && !submittableItem.hasAttribute('disabled')) {
          const value = submittableItem.getAttribute(
            'data-search-bar--token-value-modal-value-param',
          );
          const label = submittableItem.getAttribute(
            'data-search-bar--token-value-modal-label-param',
          );

          if (value) {
            this.pick({ params: { value, label } });
          }
          break;
        }

        break;
      }
    }
  }

  private onSearchBarQuery(event: unknown) {
    if (!this.active || !isEventSetQuery(event)) {
      return;
    }

    const nextLabel =
      (this.currentFormat || '{q}').replace('{q}', event.value).trim() ||
      '(start typing...)';
    this.currentQueryOption.firstElementChild!.textContent = nextLabel;
    this.currentQueryOption.removeAttribute(
      'data-search-bar--token-value-modal-label-param',
    );
    this.currentQueryOption.setAttribute(
      'data-search-bar--token-value-modal-value-param',
      event.value,
    );

    toggleAttribute(
      'disabled',
      this.currentQueryOption,
      this.currentValidator
        ? !this.currentValidator(event.value || '')
        : event.value.length === 0,
    );
    this.autocomplete(event.value);
  }

  private autocomplete(lastValue: string) {
    clearTimeout(this.autocompleteTimer);

    this.autocompleteController?.abort('Autocomplete query has changed');
    if ('AbortController' in window) {
      this.autocompleteController = new AbortController();
    }

    this.allowReselection = true;

    let execute: undefined | (() => void);
    switch (this.lastTokenType) {
      case TOKEN_TYPE_ASSIGNEE:
      case TOKEN_TYPE_AUTHOR: {
        execute = () => {
          const url = this.autocompleteUrlsValue.users;
          if (!url) {
            throw new Error('No autocomplete url available for users');
          }

          this.loading = true;

          fetch(url.replace(/{q}|%7Bq%7D/, encodeURIComponent(lastValue)), {
            method: 'GET',
            headers: {
              accept: `${MEDIA_TYPE_COMBOBOX}, application/problem+json; q=0.1`,
            },
            signal: this.autocompleteController?.signal,
          })
            .then(this.onAutocompletedResponse)
            .then(this.onAutocompleteHtml)
            .catch((error) => {
              this.loading = false;
              console.error('[search-bar] stopped autocomplete', error);
            });
        };

        break;
      }

      case TOKEN_TYPE_LABEL: {
        execute = () => {
          const url = this.autocompleteUrlsValue.labels;
          if (!url) {
            throw new Error('No autocomplete url available for labels');
          }

          this.loading = true;

          fetch(url.replace(/{q}|%7Bq%7D/, encodeURIComponent(lastValue)), {
            method: 'GET',
            headers: {
              accept: `${MEDIA_TYPE_COMBOBOX}, application/problem+json; q=0.1`,
            },
            signal: this.autocompleteController?.signal,
          })
            .then(this.onAutocompletedResponse)
            .then(this.onAutocompleteHtml)
            .catch((error) => {
              this.loading = false;
              console.error('[search-bar] stopped autocomplete', error);
            });
        };

        break;
      }

      case TOKEN_TYPE_ALIAS_ASSET_TYPE: {
        execute = () => {
          const url = this.autocompleteUrlsValue.packages;
          if (!url) {
            throw new Error('No autocomplete url available for packages');
          }

          this.loading = true;

          fetch(
            url
              .replace(/{q}|%7Bq%7D/, encodeURIComponent(lastValue))
              .replace(/{types}|%7Btypes%7D/, 'asset_role'),
            {
              method: 'GET',
              headers: {
                accept: `${MEDIA_TYPE_COMBOBOX}, application/problem+json; q=0.1`,
              },
              signal: this.autocompleteController?.signal,
            },
          )
            .then(this.onAutocompletedResponse)
            .then(this.onAutocompleteHtml)
            .catch((error) => {
              this.loading = false;
              console.error('[search-bar] stopped autocomplete', error);
            });
        };

        break;
      }

      case TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE: {
        execute = () => {
          const url = this.autocompleteUrlsValue.packages;
          if (!url) {
            throw new Error('No autocomplete url available for packages');
          }

          this.loading = true;

          fetch(
            url
              .replace(/{q}|%7Bq%7D/, encodeURIComponent(lastValue))
              .replace(/{types}|%7Btypes%7D/, 'employee_role'),
            {
              method: 'GET',
              headers: {
                accept: `${MEDIA_TYPE_COMBOBOX}, application/problem+json; q=0.1`,
              },
              signal: this.autocompleteController?.signal,
            },
          )
            .then(this.onAutocompletedResponse)
            .then(this.onAutocompleteHtml)
            .catch((error) => {
              this.loading = false;
              console.error('[search-bar] stopped autocomplete', error);
            });
        };

        break;
      }

      case TOKEN_TYPE_ROLE: {
        execute = () => {
          const url = this.autocompleteUrlsValue.packages;
          if (!url) {
            throw new Error('No autocomplete url available for packages');
          }

          this.loading = true;

          fetch(
            url
              .replace(/{q}|%7Bq%7D/, encodeURIComponent(lastValue))
              .replace(/{types}|%7Btypes%7D/, 'employee_role,asset_role'),
            {
              method: 'GET',
              headers: {
                accept: `${MEDIA_TYPE_COMBOBOX}, application/problem+json; q=0.1`,
              },
              signal: this.autocompleteController?.signal,
            },
          )
            .then(this.onAutocompletedResponse)
            .then(this.onAutocompleteHtml)
            .catch((error) => {
              this.loading = false;
              console.error('[search-bar] stopped autocomplete', error);
            });
        };

        break;
      }

      case TOKEN_TYPE_SITE:
      case TOKEN_TYPE_ALIAS_LOCATED_AT: {
        execute = () => {
          const url = this.autocompleteUrlsValue.sites;
          if (!url) {
            throw new Error('No autocomplete url available for sites');
          }

          this.loading = true;

          fetch(url.replace(/{q}|%7Bq%7D/, encodeURIComponent(lastValue)), {
            method: 'GET',
            headers: {
              accept: `${MEDIA_TYPE_COMBOBOX}, application/problem+json; q=0.1`,
            },
            signal: this.autocompleteController?.signal,
          })
            .then(this.onAutocompletedResponse)
            .then(this.onAutocompleteHtml)
            .catch((error) => {
              this.loading = false;
              console.error('[search-bar] stopped autocomplete', error);
            });
        };

        break;
      }
    }

    if (!execute) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.autocompleteTimer = setTimeout(execute, 300);
  }

  private async onAutocompletedResponse(value: Response) {
    const contentType = value.headers.get('content-type') || '';

    if (contentType.startsWith(MEDIA_TYPE_COMBOBOX)) {
      return value.text();
    }

    if (contentType.includes(`variant=${MEDIA_TYPE_COMBOBOX}`)) {
      return value.text();
    }

    throw new Error(`Expected ${MEDIA_TYPE_COMBOBOX}, actual ${contentType}`);
  }

  private onAutocompleteHtml(html: string) {
    this.loading = false;

    // Hide current options
    this.selectionGroup
      .querySelectorAll('[role="option"]')
      .forEach((option) => toggleHidden(option, true));

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    wrapper.querySelectorAll('li[role="option"]').forEach((li, index) => {
      if (index > this.maxAutocompleteOptionsValue) {
        return;
      }

      const value = li.getAttribute('data-id-value');
      if (!value) {
        return;
      }

      const id = li.id;
      const existing = this.listBox.querySelector(`#${id}`);
      if (existing) {
        toggleHidden(existing, false);
        return;
      }

      const label = createElement('div', {
        class: 'flex flex-row w-full items-center',
      });
      label.innerHTML = li.innerHTML;

      this.mappedValues[value] = label.outerHTML;
      const option = this.createOption({
        value,
        label,
      });
      option.setAttribute('id', id);

      this.selectionGroup.append(option);
    });

    toggleHidden(this.selectionGroup, false);
    emitChangedModalLayout();

    if (!this.allowReselection) {
      return;
    }

    // Move active focus to top auto complete item
    const firstElement = this.selectionGroup.querySelector<HTMLButtonElement>(
      'button:not([role="none"]):not([hidden]):enabled',
    );
    this.activeItem = firstElement;

    if (!this.activeItem) {
      this.activateFirst();
    }
  }

  public pick(event: unknown) {
    if (!isActionPickValue(event)) {
      return;
    }

    emitPickedValue(event.params.value, event.params.label);
  }

  private createOptions(
    type: TokenType,
    initialValue: string | undefined,
    initialLabel: string | undefined,
  ) {
    if (this.lastTokenType === type) {
      return;
    }

    this.lastTokenType = type;
    this.clearItems();

    const options = OPTIONS[type];
    if (options.defaults.length > 0) {
      this.addGroup(
        options.defaults.map((option) =>
          this.createOption(
            typeof option === 'string' ? { value: option } : option,
          ),
        ),
      );
    }

    this.selectionGroup = this.addGroup();
    // TODO: initial items?
    toggleHidden(
      this.selectionGroup,
      this.selectionGroup.children.length === 0,
    );

    this.currentFormat = options.format;
    this.currentValidator = options.validate;
    this.currentQueryOption = this.createOption(
      initialValue
        ? { value: initialValue, label: initialLabel }
        : { value: '', label: '(start typing...)' },
    );

    this.loadingRegion = createElement('div', {
      tabindex: -1,
      role: 'region',
      'aria-live': 'polite',
      class: `absolute top-[0.5rem] right-[0.4rem]`,
    });
    this.spinner = createSvg(
      {
        class: 'animate-spin size-5 dark:text-green-400 text-green-700',
        fill: 'none',
        viewBox: '0 0 24 24',
        role: 'image',
      },
      `
<circle class="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
<path class="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
<title>Fetching data</title>
`,
    );

    toggleAttribute(
      'disabled',
      this.currentQueryOption,
      this.currentValidator && !this.currentValidator(initialValue || ''),
    );

    const group = this.addGroup([this.currentQueryOption]);
    group.classList.add('relative');
    group.append(this.loadingRegion);
  }

  private set loading(next: boolean) {
    if (next) {
      this.loadingRegion.append(this.spinner);
    } else {
      this.spinner.remove();
    }
  }
}
